// ★ P4-a: `buildSeatDeptResolver` dropped from this import — its only use here was the
// deleted LEGACY_SEAT_RESOLVER. `contractorBucketForCatSlug` is still used below.
import { contractorBucketForCatSlug } from '../utils/seatDepartments';
// W-002: which leaves the vendor engine books is the ONE rule in lineRoles.js (it also gates every vendor write).
import { isVendorBookableSource } from './lineRoles';
import { resolvePctRevenueBase } from '../utils/pctRevenueSource';   // W-073 (b): the ONE reader of a % vendor's base

const r2 = n => Math.round(n * 100) / 100;

// ★ SWEEP B2-ii: the hardcoded 2024-2026 ALL_M window is DELETED. It was Semitek's
// model window used as a back-compat default; every caller passes allMonths
// (runFullModel always does) and no page imports the export — verified. An
// absent window now yields an empty one (honest-empty), not a foreign calendar.

// Back-compat fallback — kept so legacy callers that don't pass isLines still work.
// Source of truth when isLines is provided: isLines filtered by source.
// ★ Item 8 note: the "ga_software" below is NOT the Semitek default that item 8
// removed — it is one element of a legacy zero-init list that is DEAD whenever
// isLines is passed (runFullModel always passes it). Deleting one entry would
// leave a Semitek list minus one entry, which is worse than leaving it intact and
// labelled; retiring the whole list is a separate deprecation.
// ★ SWEEP B2-ii: IS_CAT_IDS_FALLBACK (28 hardcoded Semitek is_line keys, incl.
// ga_software) DELETED — dead for every live tenant (runFullModel always passes
// isLines) and a Semitek chart of accounts is never a safe default for anyone else.

const OI_LINES = new Set(["oi_interest","oi_taxes","oi_other_exp","oi_other_inc"]);

/* Derive the IS line IDs the vendor engine should zero-init and potentially
   populate. Uses source-based filter: any leaf with source='vendor' (vendor
   engine populates from vendor actuals/forecast) OR source='hardcoded' (e.g.
   oi_interest — injected by sweep, but vendor engine zero-inits so merge
   behavior matches). Produces Semitek's 30-item hardcoded list exactly. */
function deriveIsCatIds(isLines) {
  // ★ B2-ii: honest-empty, was Semitek's 28 keys. A caller with no chart of accounts
  // gets no vendor lines rather than another company's.
  if (!isLines) return [];
  return isLines
    .filter(l => isVendorBookableSource(l.source))
    .map(l => l.id);
}

/* ⛔ LEGACY_SEAT_RESOLVER DELETED HERE (P4-a) — together with seatDepartments' legacy arm,
   as ONE unit. It was `buildSeatDeptResolver([], true)` at module scope: a HARDCODED true
   that ran on every page load, so the arm it reached was NOT unreachable. It was inert for
   a different reason — its value was never consumed, because `computeVendorIS`'s only
   caller (runFullModel:540) always passes a real resolver and every buildSeatDeptResolver
   return is a truthy object, so the `|| LEGACY_SEAT_RESOLVER` below could never be taken.
   Deleting the arm without this line would have left a module-load call pointing at changed
   meaning — precisely what the comment that used to sit here warned about. */

/* Returns true iff vendor v is active in month m. A vendor is inactive
   when (start_date set AND m < start_date) OR (end_date set to a concrete
   value AND m > end_date). Missing/empty start_date or end_date='until_canceled'
   is treated as no gate (vendor active).

   Historical months (m <= closeMonth) are always active regardless of
   start_date/end_date — closed periods are immutable per standard
   accounting practice. A retroactive end_date edit must not change
   historical IS/BS/CFS values; only forecast months respect end_date.

   Used by both Pass 1 (contractor HC) and Pass 2 (amount aggregation). */
function isVendorActiveInMonth(v, m, closeMonth) {
  if (closeMonth && m <= closeMonth) return true;  // historical: locked, always active
  const sd = v.start_date || v.startDate;
  const ed = v.end_date || v.endDate || "until_canceled";
  if (sd && m < sd) return false;
  if (ed !== "until_canceled" && ed && m > ed) return false;
  return true;
}

/* Sum vendor monthlyData by IS category.
   The Vendor Dashboard saves complete monthlyData (actuals + forecast)
   to the DB, so this engine just reads and sums — no forecast generation.
   Exception: pct_revenue vendors get forecast months recomputed from live total_revenue.
   Exception: per_seat vendors get forecast months computed from headcount × cost per seat. */
export function computeVendorIS(vendors, totalRevenue, cutoff, payrollHC, allMonths, lineDefs, revByLine, seatDeptResolver) {
  const MONTHS = allMonths || [];
  // ★ Item 7: tenant per-seat department resolver (8th param). P4-a removed the
  // `|| LEGACY_SEAT_RESOLVER` back-compat fallback: the only caller always passes one.
  const seatDepts = seatDeptResolver;
  const CAT_IDS = deriveIsCatIds(lineDefs);
  const isLines = {};
  CAT_IDS.forEach(id => { isLines[id] = {}; MONTHS.forEach(m => { isLines[id][m] = 0; }); });

  // vendorOpex = total vendor spend excluding Other Income/Expenses (for DPO calc)
  const vendorOpex = {};
  MONTHS.forEach(m => { vendorOpex[m] = 0; });

  // ★ B3a (A3, dead site): was `|| "2025-12"` — Semitek's window edge. Not reached (the
  // caller always passes a value, verified in diffs/sweep-b3-verify.txt step 0); the
  // literal is removed so a genuinely-null date propagates honestly.
  const cut = cutoff || null;

  // ── Pass 1: Contractor headcount (needed for per-seat vendors) ──
  const HC_BUCKETS = ["contractors_cogs","contractors_sm","contractors_ga","contractors_rd","contractors_total"];
  const contractorHC = {};
  HC_BUCKETS.forEach(b => { contractorHC[b] = {}; MONTHS.forEach(m => { contractorHC[b][m] = 0; }); });

  // ★ Item 7: the bucket DECISION now lives once, in utils/seatDepartments.js, so
  // this is_line-prefix classifier and the per-seat department classifier cannot
  // diverge. Behaviour is unchanged: cogs_/sm_/ga_/rd_ → their bucket, else null.
  const catToBucket = cat => {
    const m = /^(cogs|sm|ga|rd)_/.exec(cat);
    return m ? contractorBucketForCatSlug(m[1]) : null;
  };

  vendors.forEach(v => {
    if (!v.active) return;
    if (!v.is_contractor && !v.isContractor) return;
    // ★ Item 8 (R1): honest-null guard. ModelProvider no longer coerces a missing
    // is_line to "ga_software", so this key can now be null/"" — and catToBucket
    // calls .startsWith(), which throws on null. A vendor with no IS line has no
    // contractor-HC bucket by definition; skip it. (Its spend is separately dropped
    // at :129 below and NAMED by pre-flight P5 — silent no more.)
    const catKey = v.isCat || v.is_line;
    if (!catKey) return;
    const bucket = catToBucket(catKey);
    if (!bucket) return;
    const defCount = v.contractor_count || v.contractorCount || 1;
    const monthly = v.contractor_monthly || v.contractorMonthly || {};
    const cm = typeof monthly === 'string' ? JSON.parse(monthly) : monthly;
    MONTHS.forEach(m => {
      if (!isVendorActiveInMonth(v, m, cut)) return;
      // Only count in months where vendor has spend > 0
      const spend = r2((v.monthlyData||{})[m] || 0);
      if (spend <= 0) return;
      const count = (cm[m] !== undefined && cm[m] !== null && cm[m] !== "") ? cm[m] : defCount;
      contractorHC[bucket][m] += count;
      contractorHC.contractors_total[m] += count;
    });
  });

  // ── Pass 2: Sum vendor amounts (including per-seat computation) ──
  // B6: { vendorId: { forecastMonth: amount } } for every pct_revenue vendor this engine books (active, on an IS line it carries).
  const pctVendorForecast = {};
  vendors.forEach(v => {
    if (!v.active) return;
    if (!isLines[v.isCat]) return;
    const vType = v.forecast_type || v.type;
    MONTHS.forEach(m => {
      let amt;
      if (vType === "pct_revenue" && m > cut) {
        if (!isVendorActiveInMonth(v, m, cut)) {
          amt = 0;
        } else {
          // 5a Item 2: per-line picker with strict $0 fallback.
          //   NULL pct_revenue_source → totalRevenue (combinedRevenue) — preserves
          //     current behavior for the 4 existing pct_revenue vendors
          //   non-NULL pct_revenue_source → strict revByLine[sourceKey] lookup;
          //     produces amt=0 if source key is absent from revByLine (e.g. Path A
          //     misc_revenue lines until Session 5e Path A↔B integration lands)
          //   W-073 (b), 2026-09-30: the ONE resolver (utils/pctRevenueSource.js). The stored sentinel for "Total Revenue"
          //   reads totalRevenue -- read as a line key it would book $0. NULL (unanswered) keeps the total-revenue arithmetic
          //   and an unknown key keeps its $0 (no number moves); the pre-flight's P18 names both.
          //   2026-10-27 (Brian B1 / B2): totalRevenue / revByLine are engines/revenueByLeaf.js's total / byLeaf -- "Total
          //   Revenue" is the Income Statement's Total Revenue row, and a line base is any line a revenue module forecasts onto.
          const sourceKey = v.pct_revenue_source || v.pctRevenueSource;
          const sourceMap = resolvePctRevenueBase(sourceKey, revByLine, totalRevenue).base;
          if (!sourceMap || !sourceMap[m]) {
            amt = 0;
          } else {
            const rate = parseFloat(v.pct_rate) || 0;
            amt = r2(rate * sourceMap[m]);
          }
        }
        // B6 (2026-10-27): the amount this vendor books, per forecast month -- the Vendor Dashboard shows it rather than re-computing.
        (pctVendorForecast[v.id] = pctVendorForecast[v.id] || {})[m] = amt;
      } else if (vType === "per_seat" && m > cut) {
        // Per-seat: cost × headcount — only in active months
        if (!isVendorActiveInMonth(v, m, cut)) {
          amt = 0;
        } else {
          const perSeatCost = parseFloat(v.per_seat_cost || v.perSeatCost) || 0;
          const seatBasis = v.seat_basis || v.seatBasis || "fte";
          let rawDepts = v.seat_departments || v.seatDepartments || [];
          if (typeof rawDepts === 'string') rawDepts = JSON.parse(rawDepts);
          const seatDepartments = rawDepts;
          const isCompanyWide = !seatDepartments || seatDepartments.length === 0;

          let headcount = 0;

          // FTE headcount
          if (seatBasis === "fte" || seatBasis === "both") {
            if (payrollHC) {
              if (isCompanyWide) {
                headcount += payrollHC.fte?.[m] || 0;
              } else {
                seatDepartments.forEach(dept => {
                  // ★ Item 7: tenant-derived. For a dept-mapping tenant this is the
                  // IDENTITY (payrollHC is keyed by the same slug); for a legacy
                  // tenant it is the old Semitek map. null (unknown dept) still
                  // skips — a wrong department must not book a real cost.
                  const fteKey = seatDepts.fteKey(dept);
                  if (fteKey) headcount += payrollHC[fteKey]?.[m] || 0;
                });
              }
            }
          }

          // Contractor headcount
          if (seatBasis === "contractors" || seatBasis === "both") {
            if (isCompanyWide) {
              headcount += contractorHC.contractors_total?.[m] || 0;
            } else {
              const buckets = new Set();
              seatDepartments.forEach(dept => {
                const b = seatDepts.contractorBucket(dept);   // ★ Item 7: tenant-derived
                if (b) buckets.add(b);
              });
              buckets.forEach(b => { headcount += contractorHC[b]?.[m] || 0; });
            }
          }

          amt = r2(perSeatCost * headcount);
        }
      } else {
        amt = isVendorActiveInMonth(v, m, cut) ? r2((v.monthlyData||{})[m] || 0) : 0;
      }
      isLines[v.isCat][m] = r2((isLines[v.isCat][m] || 0) + amt);
      if (!OI_LINES.has(v.isCat)) {
        vendorOpex[m] = r2(vendorOpex[m] + amt);
      }
    });
  });

  return { isLines, vendorOpex, contractorHC, pctVendorForecast };
}

export { r2 };
